import { useMemo } from 'react';

import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import type { EditorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePluginType.ts';
import { expVal } from '@confluence/feature-experiments';
import { useIsDesktop } from '@confluence/desktop-native-bridge';

type Options = {
	disabled: boolean;
	collabEditInternetLost: boolean;
	internetDisconnected: boolean;
	api: PublicPluginAPI<[EditorOfflineStoragePlugin]> | undefined;
};

/**
 * Hook to determine if we should disable the editor contents based on the current
 * state of connectivity.
 *
 * @param options Options for hook
 * @param options.disabled If the editor component is currently disabled
 * @param options.collabEditInternetLost If the collab edit has disconnected
 * @param options.internetDisconnected If the internet is disconnected
 * @param options.api Editor API to listen to the state of plugins
 * @returns boolean - if the component is disabled
 */
export const useEditorContentDisabled = ({
	disabled,
	collabEditInternetLost,
	internetDisconnected,
	api,
}: Options): boolean => {
	const { isDesktop } = useIsDesktop();

	const isOfflineStorageDisabled =
		useSharedPluginStateSelector(api, 'editorOfflineStorage.disabled', {
			disabled: !(isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false)),
		}) ?? false;

	const inactiveSyncing =
		useSharedPluginStateSelector(api, 'editorOfflineStorage.inactiveSyncing', {
			disabled: !(isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false)),
		}) ?? false;

	return useMemo(() => {
		if (isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false)) {
			if (inactiveSyncing) {
				return true;
			}
			if (isOfflineStorageDisabled) {
				return disabled;
			}
			return disabled && !(collabEditInternetLost || internetDisconnected);
		}
		return disabled;
	}, [
		isDesktop,
		collabEditInternetLost,
		internetDisconnected,
		disabled,
		isOfflineStorageDisabled,
		inactiveSyncing,
	]);
};
